import React, { useState, useId } from "react";
import { ValidationConstraints, PromptType } from "@/components/tasks/types";
import ValidationRulesSummary from "./ValidationRulesSummary";

interface ValidationEditorProps {
  validation: ValidationConstraints;
  promptType: PromptType;
  onValidationChange: (validation: ValidationConstraints) => void;
}

export default function ValidationEditor({
  validation,
  promptType,
  onValidationChange,
}: ValidationEditorProps) {
  const [expanded, setExpanded] = useState(Object.keys(validation).length > 0);
  const requiredId = useId();

  const updateValidationField = (
    field: keyof ValidationConstraints,
    value: any
  ) => {
    const newValidation = { ...validation, [field]: value };

    // Remove field if value is empty/false/0
    if (
      value === false ||
      value === "" ||
      value === 0 ||
      value === undefined
    ) {
      delete newValidation[field];
    }

    onValidationChange(newValidation);
  };

  return (
    <div className="space-y-4 bg-blue-50 p-4 rounded-lg border border-blue-100">
      <h5
        className="text-sm font-medium text-blue-800 uppercase tracking-wide cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? "▼ " : "▶ "}
        Validation Rules
      </h5>

      {expanded && (
        <>
          {/* Required Field */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id={requiredId}
              checked={validation.required || false}
              onChange={(e) =>
                updateValidationField("required", e.target.checked)
              }
              className="text-blue-600 focus:ring-blue-500 rounded"
            />
            <label
              htmlFor={requiredId}
              className="text-sm font-medium text-gray-700"
            >
              Required {promptType === "checkbox" ? "(must be checked)" : ""}
            </label>
          </div>

          {/* String length constraints for text fields */}
          {(promptType === "single-line-textbox" ||
            promptType === "multi-line-textbox") && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Length
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={validation.minLength || ""}
                    onChange={(e) =>
                      updateValidationField(
                        "minLength",
                        parseInt(e.target.value) || undefined
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Length
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={validation.maxLength || ""}
                    onChange={(e) =>
                      updateValidationField(
                        "maxLength",
                        parseInt(e.target.value) || undefined
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Pattern validation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pattern (Regular Expression)
                </label>
                <input
                  type="text"
                  value={validation.pattern || ""}
                  onChange={(e) =>
                    updateValidationField("pattern", e.target.value || undefined)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., ^[a-zA-Z]+$ for letters only"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Common patterns: Email:{" "}
                  <code>^[\w-\.]+@([\w-]+\.)+[\w-]{`{2,4}`}$</code>, Phone:{" "}
                  <code>^\+?[1-9]\d{`{1,14}`}$</code>
                </p>
              </div>
            </>
          )}

          {/* Numeric constraints for number-like fields */}
          {promptType === "numeric" && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Value
                </label>
                <input
                  type="number"
                  value={validation.min ?? ""}
                  onChange={(e) =>
                    updateValidationField(
                      "min",
                      parseFloat(e.target.value) || undefined
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="For numeric fields"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maximum Value
                </label>
                <input
                  type="number"
                  value={validation.max ?? ""}
                  onChange={(e) =>
                    updateValidationField(
                      "max",
                      parseFloat(e.target.value) || undefined
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="For numeric fields"
                />
              </div>
            </div>
          )}

          {/* Custom error message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Custom Error Message
            </label>
            <input
              type="text"
              value={validation.customMessage || ""}
              onChange={(e) =>
                updateValidationField(
                  "customMessage",
                  e.target.value || undefined
                )
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Optional custom validation error message"
            />
            <p className="text-xs text-gray-500 mt-1">
              If not provided, default messages will be used for each validation
              rule.
            </p>
          </div>

          {/* Validation Summary */}
          <ValidationRulesSummary validation={validation} />
        </>
      )}
    </div>
  );
}
